import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useMe, useMetrics, useSession, useSignOut, useStorySummary } from '../api/hooks';
import type { AdminRole } from '../api/types';
import { Button, Count, Icon, Plate, Toaster, type IconName } from '../ui';
import { CommandPalette, NAV_PAGES } from './CommandPalette';
import { ROLE_LABEL } from './labels';
import { useTheme } from './theme';
import s from './Shell.module.css';

export type NavItem = { to: string; label: string; icon: IconName; end?: true; role?: AdminRole };

/** Grouped like the work: running the game, its players, the quality of what it makes, then settings. */
export const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: '營運',
    items: [
      { to: '/', label: '總覽', icon: 'dashboard', end: true },
      { to: '/insights', label: '數據洞察', icon: 'chart' },
      { to: '/ai-cost', label: 'AI 成本', icon: 'coin', role: 'support' },
      { to: '/growth', label: '預約與分享', icon: 'heart' },
      { to: '/jobs', label: 'Jobs 佇列', icon: 'jobs' },
      { to: '/notifications', label: '推送通知', icon: 'mail', role: 'support' },
    ],
  },
  {
    group: '玩家',
    items: [
      { to: '/users', label: '玩家', icon: 'users', role: 'support' },
      { to: '/moderation', label: '檢舉審核', icon: 'flag', role: 'moderator' },
    ],
  },
  {
    group: '內容品質',
    items: [
      { to: '/story', label: '故事 QA', icon: 'book' },
      { to: '/manga', label: '漫畫', icon: 'image' },
      { to: '/content', label: '劇本數據', icon: 'chart' },
    ],
  },
  {
    group: '設定',
    items: [
      { to: '/app-config', label: 'App 設定', icon: 'sliders' },
      { to: '/settings', label: '伺服器設定', icon: 'coin' },
      { to: '/flags', label: '功能開關', icon: 'power' },
      { to: '/team', label: '團隊與權限', icon: 'shield', role: 'owner' },
      { to: '/audit', label: '審計日誌', icon: 'list' },
    ],
  },
];

const THEME = { light: { icon: 'sun', label: '淺色' }, dark: { icon: 'moon', label: '深色' }, system: { icon: 'sun', label: '跟隨系統' } } as const;
/** The server's role order (ADMIN_ROLES): a higher role includes the lower ones. */
export const ROLE_RANK: Record<AdminRole, number> = { viewer: 0, support: 1, moderator: 2, content: 3, owner: 4 };

export function Shell() {
  const session = useSession();
  const signOut = useSignOut();
  const me = useMe();
  const { pref, cycle } = useTheme();
  const [palette, setPalette] = useState(false);
  // Shared queries (with their own refresh) for the badges.
  const metrics = useMetrics();
  const story = useStorySummary();
  const counts: Record<string, number> = {
    '/moderation': metrics.data?.safety.openReports ?? 0,
    '/jobs': metrics.data?.jobs.failedLast24h ?? 0,
    '/story': story.data?.failed ?? 0,
  };
  const roles = me.data?.roles ?? [];
  const can = (role?: AdminRole) => !role || roles.some((r) => ROLE_RANK[r] >= ROLE_RANK[role]);
  const topRole = [...roles].sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a])[0];

  // ⌘K / Ctrl+K anywhere opens the search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={s.shell}>
      <aside className={s.sidebar}>
        <div className={s.sidebarInner}>
          <Link to="/" className={s.brand} aria-label="落筆成戀 營運後台">
            <Wordmark />
            <span className={s.brandSub}>營運後台</span>
          </Link>
          <nav className={s.nav} aria-label="頁面">
            {NAV.map((g) => {
              const items = g.items.filter((n) => can(n.role));
              if (!items.length) return null;
              return (
                <div key={g.group} className={s.navGroup}>
                  <div className={s.navGroupLabel}>{g.group}</div>
                  {items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={!!n.end}>
                      <Icon name={n.icon} size={19} />
                      <span className={s.navLabel}>{n.label}</span>
                      <Count n={counts[n.to] ?? 0} />
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>
        </div>
      </aside>
      <div className={s.column}>
        <header className={s.topbar}>
          <button type="button" className={s.search} onClick={() => setPalette(true)}>
            <Icon name="search" size={18} />
            <span>搜尋玩家：電郵、ID、好友碼</span>
            <kbd>⌘K</kbd>
          </button>
          <div className={s.topRight}>
            <button type="button" className={s.themeBtn} onClick={cycle} title={`主題：${THEME[pref].label}（按此切換）`}>
              <Icon name={THEME[pref].icon} size={18} />
            </button>
            <div className={s.who}>
              <span className={s.whoEmail} title={session?.email}>
                {session?.email}
              </span>
              {topRole && <span className={s.whoRole}>{ROLE_LABEL[topRole]}</span>}
            </div>
            <Button size="sm" onClick={() => signOut.mutate()} busy={signOut.isPending}>
              <Icon name="logout" size={16} /> 登出
            </Button>
          </div>
        </header>
        <main className={s.main}>
          <Outlet />
        </main>
      </div>
      <CommandPalette open={palette} onClose={() => setPalette(false)} pages={NAV_PAGES(NAV, can)} canSearchPlayers={can('support')} />
      <Toaster />
    </div>
  );
}

/** The calligraphy wordmark (public/art/wordmark.webp); the name in type when the art is missing. */
export function Wordmark({ className }: { className?: string }) {
  const [broken, setBroken] = useState(false);
  return broken ? (
    <span className={s.wordmarkText}>
      落筆成<em>戀</em>
    </span>
  ) : (
    <img className={className ?? s.wordmark} src="/art/wordmark.webp" alt="落筆成戀 Inked in Love" onError={() => setBroken(true)} />
  );
}

export function PageHeader({ title, description, actions, crumbs }: { title: string; description?: ReactNode; actions?: ReactNode; crumbs?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · 落筆成戀後台`;
  }, [title]);
  return (
    <header className={s.pageHead}>
      <div className={s.pageTitle}>
        {crumbs && <div className={s.crumbs}>{crumbs}</div>}
        <h1>
          <Plate large>{title}</Plate>
        </h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </header>
  );
}
